import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import axios from 'axios';
import '../styles/Leagues.css';
import '../styles/AdminLeagues.css';

function AdminLeagueDetail() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const [league, setLeague] = useState(null);
  const [draftPicks, setDraftPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedManager, setExpandedManager] = useState(null);
  const [standings, setStandings] = useState([]);
  const [scoringTable, setScoringTable] = useState(null);
  const [scoringRounds, setScoringRounds] = useState([]);
  const [scoringModel, setScoringModel] = useState('rounds-won');
  const [playerResults, setPlayerResults] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    if (!user?.isSiteAdmin) return;
    fetchAll();
  }, [id, user]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchLeague(), fetchDraftPicks(), fetchStandings(), fetchResults()]);
    setLoading(false);
  };

  const fetchLeague = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/leagues/${id}`);
      if (res.data.success) setLeague(res.data.league);
      else setError(res.data.message);
    } catch (err) {
      setError('Failed to load league');
    }
  };

  const fetchDraftPicks = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/leagues/${id}/picks`);
      if (res.data.success) setDraftPicks(res.data.picks);
    } catch {
      // picks will be empty
    }
  };

  const fetchStandings = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/leagues/${id}/standings`);
      if (res.data.success) {
        setStandings(res.data.standings);
        if (res.data.scoringTable) setScoringTable(res.data.scoringTable);
        if (res.data.rounds) setScoringRounds(res.data.rounds);
        if (res.data.scoringModel) setScoringModel(res.data.scoringModel);
      }
    } catch {
      // standings will be empty
    }
  };

  const fetchResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/leagues/${id}/results`);
      if (res.data.success) {
        const map = {};
        for (const r of res.data.results) {
          map[r.player._id] = {
            roundsWon:      r.roundsWon      || [],
            roundPositions: r.roundPositions || [],
          };
        }
        setPlayerResults(map);
      }
    } catch {
      // results will be empty
    }
  };

  const getManagerStats = (managerId) => {
    const picks = draftPicks.filter(p => p.manager._id === managerId);
    const spent = picks.reduce((sum, p) => sum + p.costCredits, 0);
    const budget = league?.draftBudget || 175;
    return { picks, spent, remaining: budget - spent };
  };

  // Build player → points map from standings for results table
  const playerPointsMap = {};
  for (const entry of standings) {
    for (const p of entry.players) {
      playerPointsMap[p.player._id] = p.points;
    }
  }

  const hasAnyResults = Object.keys(playerResults).length > 0;

  if (!user?.isSiteAdmin) {
    return (
      <div className="leagues-container">
        <p className="status-msg error">Access denied. Site Admin only.</p>
      </div>
    );
  }

  if (loading) return <div className="leagues-container"><p className="status-msg">Loading...</p></div>;
  if (error) return <div className="leagues-container"><p className="status-msg error">{error}</p></div>;
  if (!league) return null;

  return (
    <div className="leagues-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to="/admin/leagues" className="btn btn-secondary">← All Leagues</Link>
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="leagues-content">
        {/* Admin Read-Only Banner */}
        <div className="admin-readonly-banner">
          🛡️ Admin View — Read Only
        </div>

        <header className="league-detail-header">
          <div>
            <h2>{league.name}</h2>
          </div>
          <div className="league-meta">
            <span>{sportConfig?.appEmoji || '🏆'} {league.tournament?.name || 'No tournament'}</span>
            <span>📋 Status: <strong>{league.draftStatus}</strong></span>
            <span>💰 Budget: {league.draftBudget || 175} credits per manager</span>
            <span>👥 {league.managers.length} / {league.maxManagers} managers</span>
          </div>
        </header>

        {/* Tournament Info */}
        {league.tournament && (
          <div className="tournament-info-section">
            <h3>🏆 Tournament Details</h3>
            <div className="tournament-details">
              <span>📍 {league.tournament.location}</span>
              <span>🏟️ {league.tournament.surface}</span>
              <span>📅 {new Date(league.tournament.startDate).toLocaleDateString()} — {new Date(league.tournament.endDate).toLocaleDateString()}</span>
              <span>🏷️ {league.tournament.category}</span>
            </div>
          </div>
        )}

        {/* Managers List with Draft Info */}
        <div className="managers-section">
          <h3>👥 Managers</h3>
          <div className="managers-list">
            {league.managers.map(member => {
              const stats = getManagerStats(member._id);
              const isExpanded = expandedManager === member._id;

              return (
                <div key={member._id} className="manager-card">
                  <div className="manager-row">
                    <div className="manager-info">
                      <span className="manager-name">
                        {member.firstName ? `${member.firstName} ${member.lastName || ''}` : member.username}
                      </span>
                      <span className="manager-username">@{member.username}</span>
                      {(member._id === league.commissioner._id
                        || (league.coCommissioners || []).some(c => c._id === member._id)) && (
                        <span className="role-badge">Commissioner</span>
                      )}
                    </div>
                    <div className="manager-draft-stats">
                      <span className="draft-stat">🎯 {stats.picks.length} players</span>
                      <span className="draft-stat">💰 {stats.remaining} cr left</span>
                      {stats.picks.length > 0 && (
                        <button
                          className="btn btn-small btn-secondary-outline"
                          onClick={() => setExpandedManager(isExpanded ? null : member._id)}
                        >
                          {isExpanded ? 'Hide' : 'View'} Team
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && stats.picks.length > 0 && (
                    <div className="manager-team">
                      <table className="team-table">
                        <thead>
                          <tr>
                            <th>Pick</th>
                            <th>Player</th>
                            <th>Tour</th>
                            <th>Rank</th>
                            <th>Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.picks.map(pick => (
                            <tr key={pick._id}>
                              <td>#{pick.pickNumber}</td>
                              <td className="player-name">
                                <a
                                  href={`https://google.com/search?q=${encodeURIComponent(pick.player.name)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {pick.player.name}
                                </a>
                              </td>
                              <td>
                                <span className={`tour-badge ${pick.player.tour.toLowerCase()}`}>
                                  {pick.player.tour}
                                </span>
                              </td>
                              <td>#{pick.player.ranking}</td>
                              <td>{pick.costCredits} cr</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="team-total">
                        Total: {stats.spent} / {league.draftBudget || 175} credits spent
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Draft Log */}
        {draftPicks.length > 0 && (
          <div className="draft-log-section">
            <h3>📜 Draft Log</h3>
            <div className="draft-log">
              {draftPicks.map(pick => (
                <div key={pick._id} className="draft-log-entry">
                  <span className="pick-number">#{pick.pickNumber}</span>
                  <span className="pick-manager">
                    {pick.manager.firstName || pick.manager.username}
                  </span>
                  <span className="pick-arrow">→</span>
                  <span className="pick-player">
                    <a
                      href={`https://google.com/search?q=${encodeURIComponent(pick.player.name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pick.player.name}
                    </a>
                  </span>
                  <span className={`tour-badge ${pick.player.tour.toLowerCase()}`}>
                    {pick.player.tour}
                  </span>
                  <span className="pick-cost">{pick.costCredits} cr</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Standings */}
        {standings.length > 0 && (
          <div className="standings-section">
            <h3>🏆 Standings</h3>
            <table className="standings-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Manager</th>
                  <th>Players</th>
                  <th>Points</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((entry, idx) => (
                  <tr key={entry.manager._id}>
                    <td className="standings-rank">{idx + 1}</td>
                    <td>{entry.manager.firstName || entry.manager.username}</td>
                    <td>{entry.players.length}</td>
                    <td className="standings-points">{entry.totalPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {standings.map((entry, idx) => (
              entry.players.length > 0 && entry.players.some(p => p.points > 0) && (
                <details key={entry.manager._id} className="standings-detail">
                  <summary>
                    #{idx + 1} {entry.manager.firstName || entry.manager.username} — {entry.totalPoints} pts breakdown
                  </summary>
                  <div className="standings-breakdown">
                    {entry.players
                      .sort((a, b) => b.points - a.points)
                      .map(p => (
                        <div key={p.player._id} className="breakdown-row">
                          <span className="breakdown-player">
                            <a
                              href={`https://google.com/search?q=${encodeURIComponent(p.player.name)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {p.player.name}
                            </a>
                          </span>
                          <span className={`tour-badge ${p.player.tour.toLowerCase()}`}>{p.player.tour}</span>
                          <span className="breakdown-rounds">
                            {(scoringModel || 'rounds-won') === 'rounds-won'
                              ? (p.roundsWon?.join(', ') || '—')
                              : (p.roundPositions?.map(rp => `${rp.round}:#${rp.position}`).join(', ') || '—')
                            }
                          </span>
                          <span className="breakdown-points">{p.points} pts</span>
                        </div>
                      ))}
                  </div>
                </details>
              )
            ))}
          </div>
        )}

        {/* Read-Only Tournament Results */}
        {draftPicks.length > 0 && hasAnyResults && (
          <div className="results-entry-section">
            <h3>📊 Tournament Results</h3>
            {scoringTable && scoringModel === 'rounds-won' && (
              <p className="results-instructions">
                Scoring: {Object.entries(scoringTable).map(([r, p]) => `${r}=${p}`).join(', ')}
              </p>
            )}
            <div className="results-table-wrapper">
              <table className="results-entry-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Tour</th>
                    <th>{scoringModel === 'rounds-won' ? 'Rounds Won' : 'Round Positions'}</th>
                    <th>Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Map(draftPicks.map(p => [p.player._id, p.player])).values())
                    .sort((a, b) => a.tour.localeCompare(b.tour) || a.ranking - b.ranking)
                    .map(player => {
                      const result = playerResults[player._id] || { roundsWon: [], roundPositions: [] };
                      const pts = playerPointsMap[player._id] || 0;
                      return (
                        <tr key={player._id}>
                          <td className="player-name">
                            <a
                              href={`https://google.com/search?q=${encodeURIComponent(player.name)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {player.name}
                            </a>
                          </td>
                          <td>
                            <span className={`tour-badge ${player.tour.toLowerCase()}`}>
                              {player.tour}
                            </span>
                          </td>
                          <td className="readonly-rounds">
                            {scoringModel === 'rounds-won'
                              ? (result.roundsWon.length > 0
                                  ? result.roundsWon.map(r => (
                                      <span key={r} className="round-won-badge">{r}</span>
                                    ))
                                  : <span className="no-rounds">—</span>
                                )
                              : (result.roundPositions.length > 0
                                  ? result.roundPositions.map(rp => (
                                      <span key={rp.round} className="round-won-badge">{rp.round}:#{rp.position}</span>
                                    ))
                                  : <span className="no-rounds">—</span>
                                )
                            }
                          </td>
                          <td className="result-points">{pts}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminLeagueDetail;
